import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { blogPosts } from "@/data/blogPosts";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";


function renderBlock(b, i) {
  if (b.type === "h") {
    return <h2 key={i} className="font-display text-2xl md:text-3xl font-bold text-[#0A1B5E] mt-10 mb-4">{b.text}</h2>;
  }
  if (b.type === "ul") {
    return (
      <ul key={i} className="my-6 space-y-3">
        {b.items.map((it, j) => (
          <li key={j} className="flex items-start gap-3 text-slate-700 leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#3B82F6] shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return <p key={i} className="text-slate-700 leading-relaxed text-[17px] mb-5">{b.text}</p>;
}

export default function BlogPost() {
  const { id } = useParams();
  const [data, setData] = useState({ post: null, related: [] });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);

        const post = blogPosts.find((p) => p.id === id);

        if (!post) {
            setNotFound(true);
            setLoading(false);
            return;
        }

        const related = blogPosts
            .filter((p) => p.category === post.category && p.id !== id)
            .slice(0, 3);

        setData({
            post,
            related
        });

        setLoading(false);
    }, [id]);

  if (loading) {
    return <div className="container-cd py-32 text-center text-slate-500" data-testid="blogpost-loading">Loading article…</div>;
  }
  if (notFound || !data.post) {
    return (
      <div className="container-cd py-32 text-center" data-testid="blogpost-notfound">
        <p className="text-slate-600 mb-6">Article not found.</p>
        <Link to="/blog" className="btn-primary">Back to all articles <ArrowRight size={14} /></Link>
      </div>
    );
  }

  const { post, related } = data;

  return (
    <div data-testid="blogpost-page">
      <PageHero
        eyebrow={post.category}
        title={post.title}
        description={post.excerpt}
      >
        <div className="flex items-center gap-5 text-sm text-slate-500 font-mono-tech tracking-wider">
          <span className="flex items-center gap-2"><Calendar size={14} /> {post.date}</span>
          <span className="flex items-center gap-2"><Clock size={14} /> {post.read_time}</span>
        </div>
      </PageHero>

      <article className="py-16 bg-white">
        <div className="container-cd max-w-4xl">
          <div className="rounded-[1.75rem] overflow-hidden border border-slate-200 mb-12 shadow-[0_30px_80px_-30px_rgba(10,27,94,0.2)]">
            <img src={post.image} alt={post.title} className="w-full h-[420px] object-cover" data-testid="blogpost-image" />
          </div>
          <div className="prose-cd" data-testid="blogpost-content">
            {post.content?.map((b, i) => renderBlock(b, i))}
          </div>

          <div className="mt-16 pt-10 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
            <Link to="/blog" className="btn-ghost" data-testid="blogpost-back">
              <ArrowLeft size={14} /> All articles
            </Link>
            <Link to="/contact?type=consultation" className="btn-accent" data-testid="blogpost-cta">
              Talk to a Specialist <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </article>

      {related?.length > 0 && (
        <section className="py-20 bg-[#F8FAFC]" data-testid="blogpost-related">
          <div className="container-cd">
            <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0A1B5E]">Related articles</h3>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, i) => (
                <Link to={`/blog/${p.id}`} key={p.id} data-testid={`blogpost-related-${i}`} className="card-cd !p-0 overflow-hidden flex flex-col group">
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-4 left-4 badge-cd !bg-white">{p.category}</span>
                  </div>
                  <div className="p-6">
                    <div className="font-mono-tech text-xs uppercase tracking-widest text-slate-400">{p.date} · {p.read_time}</div>
                    <h4 className="mt-3 font-display text-base font-bold text-[#0A1B5E] leading-snug group-hover:text-[#3B82F6] transition-colors">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA />
    </div>
  );
}
